// Locale persistence + lookup. Mirrors utils/theme.ts: a localStorage-backed
// preference reflected onto the <html> element, resolved once at boot. Adds a
// `?lang=` URL reflection for shareable links + hreflang, and a `t()` lookup
// over the compiled message catalog.
//
// Code-splitting: only the EN catalog is in the main bundle. ES and PT-BR are
// lazy-loaded via dynamic import() on first use — keeps the initial JS under
// the 30 kB gzip budget. The catalog is always loaded before `t()` is called
// (initLocale awaits the import; setLocale awaits before dispatching the
// re-render event).
//
// WAF-safe: `lang` is a strict 3-value enum. Anything read from the URL or
// storage is clamped to that enum (unknown values are ignored, never echoed),
// and only canonical values are ever written to the URL — so no freeform text
// reaches the address bar to trip Cloud Armor's 941xxx/942xxx rules.

import { en, type Locale, type Messages, type MsgKey } from './messages';

export type { Locale, MsgKey } from './messages';
export { LOCALES } from './messages';

const KEY = 'locale';
export const LOCALE_EVENT = 'locale-changed';

let current: Locale = 'en';
let catalog: Messages = en;

function clamp(v: string | null | undefined): Locale | null {
  return v === 'en' || v === 'es' || v === 'pt-BR' ? v : null;
}

function fromUrl(): Locale | null {
  try {
    return clamp(new URLSearchParams(location.search).get('lang'));
  } catch {
    return null;
  }
}

function fromStorage(): Locale | null {
  try {
    return clamp(localStorage.getItem(KEY));
  } catch {
    // private-mode storage failure — no preference
    return null;
  }
}

function fromNavigator(): Locale {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const t = (tag ?? '').toLowerCase();
    if (t.startsWith('pt')) return 'pt-BR';
    if (t.startsWith('es')) return 'es';
    if (t.startsWith('en')) return 'en';
  }
  return 'en';
}

// Resolution precedence: an explicit ?lang= (shared link) wins, then the stored
// choice, then the browser's languages, then English.
export function getStoredLocale(): Locale {
  return fromUrl() ?? fromStorage() ?? fromNavigator();
}

export function getLocale(): Locale {
  return current;
}

// BCP-47, region-qualified tags for Intl / toLocale* formatting. The catalog
// Locale 'es' alone resolves to es-ES at runtime — Spain's grouping (1.234,5)
// and date order — not the es-MX our copy targets; 'pt-BR' and 'en' are already
// region-safe. Formatting call sites use this; catalog lookups and locale
// comparisons keep the bare Locale so they stay keyed to the catalog (#252).
const INTL_LOCALE: Record<Locale, string> = { en: 'en-US', es: 'es-MX', 'pt-BR': 'pt-BR' };

// The BCP-47 tag for the active locale — pass to Intl.NumberFormat /
// Intl.DateTimeFormat / Number#toLocaleString / Date#toLocale* (#252).
export function intlLocale(): string {
  return INTL_LOCALE[current];
}

// Locale-grouped integer/decimal formatting (12,480 · 1.234.567). Reports
// rendered raw counts with String(), dropping grouping entirely (#252).
export function fmt(n: number, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(intlLocale(), opts).format(n);
}

// og:locale values per locale (region-qualified for Open Graph). Not catalog
// copy, so kept here as a static map.
const OG_LOCALE: Record<Locale, string> = { en: 'en_US', es: 'es_MX', 'pt-BR': 'pt_BR' };
const CANONICAL_BASE = 'https://www.bluefieldchem.com/';

// Public share-target routes: they carry their own tab title AND their own
// og:*/canonical, since a shared /contact or /privacy link should scrape that
// page's own metadata (#218).
const SHARE_ROUTE: Record<string, MsgKey> = {
  '/contact': 'seo.route.contact',
  '/privacy': 'seo.route.privacy',
  '/login': 'seo.route.login',
};

// Authed/internal sections carry their own tab title — matched by path prefix so
// sub-routes (/console/security, /leads/<id>) inherit the section title — but
// KEEP the site-wide share metadata, so an internal path never becomes a shared
// link's og:url identity (#279). The prefixes are mutually exclusive roots.
const SECTION_TITLE: ReadonlyArray<readonly [string, MsgKey]> = [
  ['/leads', 'seo.route.leads'],
  ['/reports', 'seo.route.reports'],
  ['/admin', 'seo.route.users'],
  ['/console', 'seo.route.console'],
  ['/settings', 'seo.route.devices'],
  ['/me', 'seo.route.account'],
];

// The tab-title catalog key for a path: an exact public share route, else the
// authed section it falls under, else null → the site-wide home title.
function titleKey(path: string): MsgKey | null {
  if (path in SHARE_ROUTE) return SHARE_ROUTE[path];
  for (const [prefix, key] of SECTION_TITLE) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return key;
  }
  return null;
}

let headPath = '/';

// The router reports each completed navigation so the head tracks the route;
// locale switches re-apply for the same path.
export function setHeadPath(path: string): void {
  headPath = path;
  applyHead(current);
}

function setMeta(selector: string, content: string): void {
  const el = document.head?.querySelector(selector);
  if (el) el.setAttribute('content', content);
}

// applyHead rewrites the per-locale <head> SEO tags — <title>, meta description,
// and the Open Graph title/description/locale/url — from the resolved locale's
// catalog (#206). index.html ships English defaults for first paint; this
// updates them once the locale is known and on every switch, so a ?lang=-scoped
// crawl and the language switcher both see matching metadata. A tag absent from
// the document (e.g. a test that didn't load index.html) is skipped.
function applyHead(l: Locale): void {
  // document.title reflects every known route (public + authed); an unmapped
  // path falls back to the site-wide home title (#279).
  document.title = catalog[titleKey(headPath) ?? 'seo.title'];
  setMeta('meta[name="description"]', catalog['seo.description']);
  // og:*/canonical stay site-wide EXCEPT on public share-target routes, so an
  // authed/internal path is never scraped as a shared link's identity (#279).
  const shareKey = SHARE_ROUTE[headPath];
  setMeta('meta[property="og:title"]', catalog[shareKey ?? 'seo.title']);
  setMeta('meta[property="og:description"]', catalog['seo.ogDescription']);
  setMeta('meta[property="og:locale"]', OG_LOCALE[l]);
  // og:url mirrors the shareable URL: mapped share route + the ?lang= variant;
  // English on the home route is the clean canonical.
  const url = shareKey ? CANONICAL_BASE + headPath.slice(1) : CANONICAL_BASE;
  setMeta('meta[property="og:url"]', l === 'en' ? url : `${url}?lang=${l}`);
}

function apply(l: Locale): void {
  current = l;
  document.documentElement.lang = l;
  document.documentElement.setAttribute('data-locale', l);
  applyHead(l);
}

function persist(l: Locale): void {
  try {
    localStorage.setItem(KEY, l);
  } catch {
    // ignore — preference just won't survive the session
  }
}

// Reflect the active locale into ?lang= for shareable links and hreflang.
// English is the x-default, so it carries no param (clean canonical URL).
function syncUrl(l: Locale): void {
  try {
    const url = new URL(location.href);
    if (l === 'en') url.searchParams.delete('lang');
    else url.searchParams.set('lang', l);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  } catch {
    // ignore — URL reflection is a nicety, not load-bearing
  }
}

async function loadCatalog(l: Locale): Promise<Messages> {
  if (l === 'es') return (await import('./messages-es')).default;
  if (l === 'pt-BR') return (await import('./messages-pt-BR')).default;
  return en;
}

// Boot: resolve and apply the locale before the first route renders. Awaits the
// catalog import so `t()` is ready by the time the router mounts.
export async function initLocale(): Promise<void> {
  const l = getStoredLocale();
  catalog = await loadCatalog(l);
  apply(l);
  persist(l);
}

export async function setLocale(l: Locale): Promise<void> {
  if (l === current) return;
  catalog = await loadCatalog(l);
  apply(l);
  persist(l);
  syncUrl(l);
  window.dispatchEvent(new CustomEvent(LOCALE_EVENT, { detail: l }));
}

export function t(key: MsgKey): string {
  return catalog[key];
}
